import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ChevronLeft, Send, CheckCircle2, ClipboardCheck, Clock, ExternalLink, Activity } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DataPagination } from '@/components/DataPagination';

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];

type RosterCreator = {
  id: string;
  name: string | null;
  email: string | null;
  tiktok_username: string | null;
  tiktok_url: string | null;
  avatar_url: string | null;
  creator_gmv: number | null;
  follower_count: number | null;
  categories: string[] | null;
  creator_level: string | null;
};

type CreatorActivityStats = {
  creator_id: string;
  jobs_applied_count: number;
  invites_accepted_count: number;
  jobs_completed_count: number;
  last_active_at: string | null;
};

type CreatorHire = {
  id: string;
  status: string;
  package_size: number;
  creator_rate_cents: number;
  invited_at: string | null;
  confirmed_at: string | null;
  completed_at: string | null;
  cr_campaigns: { name: string | null; product_name: string | null } | null;
};

type CreatorApplication = {
  id: string;
  status: string;
  applied_at: string | null;
  reviewed_at: string | null;
  reject_reason: string | null;
  approved_hire_id: string | null;
  cr_campaigns: { name: string | null; product_name: string | null } | null;
};

function fmtCents(n: number | null | undefined) {
  if (n == null) return '—';
  return `$${(n / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function StatCard({ icon: Icon, label, value, color }: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
  color?: string;
}) {
  return (
    <div className="rounded-lg border bg-card p-4 space-y-2">
      <div className="flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</p>
      </div>
      <p className={`text-2xl font-bold ${color ?? ''}`}>{value}</p>
    </div>
  );
}

function HireStatusBadge({ status }: { status: string }) {
  if (status === 'invited')       return <Badge variant="outline">Invited</Badge>;
  if (status === 'waitlisted')    return <Badge className="bg-amber-100 text-amber-800 border border-amber-200">Waitlisted</Badge>;
  if (status === 'confirmed')     return <Badge className="bg-blue-100 text-blue-800 border border-blue-200">Confirmed</Badge>;
  if (status === 'completed')     return <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">Completed</Badge>;
  if (status === 'declined')      return <Badge className="bg-red-100 text-red-800 border border-red-200">Declined</Badge>;
  if (status === 'expired_offer') return <Badge className="bg-slate-100 text-slate-600 border border-slate-200">Expired</Badge>;
  if (status === 'failed')        return <Badge className="bg-orange-100 text-orange-800 border border-orange-200">Failed</Badge>;
  if (status === 'cancelled')     return <Badge className="bg-red-100 text-red-800 border border-red-200">Cancelled</Badge>;
  return <Badge variant="outline">{status}</Badge>;
}

function ApplicationStatusBadge({ status }: { status: string }) {
  if (status === 'pending')   return <Badge className="bg-amber-100 text-amber-800 border border-amber-200">Pending</Badge>;
  if (status === 'approved')  return <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">Approved</Badge>;
  if (status === 'rejected')  return <Badge className="bg-red-100 text-red-800 border border-red-200">Rejected</Badge>;
  if (status === 'completed') return <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">Completed</Badge>;
  if (status === 'expired')   return <Badge className="bg-slate-100 text-slate-600 border border-slate-200">Expired</Badge>;
  if (status === 'withdrawn') return <Badge className="bg-slate-100 text-slate-600 border border-slate-200">Withdrawn</Badge>;
  return <Badge variant="outline">{status}</Badge>;
}

export default function RosterCreatorDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [hiresPage, setHiresPage] = useState(1);
  const [hiresPageSize, setHiresPageSize] = useState(10);
  const [applicationsPage, setApplicationsPage] = useState(1);
  const [applicationsPageSize, setApplicationsPageSize] = useState(10);

  const { data: creator, isLoading: creatorLoading } = useQuery<RosterCreator>({
    queryKey: ['roster-creator', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('roster_creators')
        .select('id, name, email, tiktok_username, tiktok_url, avatar_url, creator_gmv, follower_count, categories, creator_level')
        .eq('id', id)
        .single();
      if (error) throw error;
      return data as RosterCreator;
    },
    enabled: !!id,
  });

  // Live participation stats — computed on read from roster_creator_activity_stats,
  // never cached, so this can't drift from the underlying event log.
  const { data: activityStats } = useQuery<CreatorActivityStats | null>({
    queryKey: ['creator-activity-stats', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('roster_creator_activity_stats')
        .select('*')
        .eq('creator_id', id)
        .maybeSingle();
      if (error) throw error;
      return data as CreatorActivityStats | null;
    },
    enabled: !!id,
  });

  // Every hire this creator has ever had, across every campaign — each row
  // links to its own HireDetail page for the per-hire invite/lifecycle timeline.
  // Server-side paginated: only the current page is fetched, not the full set.
  const { data: hiresResult, isLoading: hiresLoading } = useQuery<{ rows: CreatorHire[]; total: number }>({
    queryKey: ['creator-hires', id, hiresPage, hiresPageSize],
    queryFn: async () => {
      const { data, count, error } = await supabase
        .from('cr_hires')
        .select('id, status, package_size, creator_rate_cents, invited_at, confirmed_at, completed_at, cr_campaigns(name, product_name)', { count: 'exact' })
        .eq('creator_id', id)
        .order('invited_at', { ascending: false })
        .range((hiresPage - 1) * hiresPageSize, hiresPage * hiresPageSize - 1);
      if (error) throw error;
      return { rows: (data ?? []) as unknown as CreatorHire[], total: count ?? 0 };
    },
    enabled: !!id,
  });
  const hires = hiresResult?.rows ?? [];
  const hiresTotal = hiresResult?.total ?? 0;
  const hiresTotalPages = Math.max(1, Math.ceil(hiresTotal / hiresPageSize));

  // Every application this creator has ever submitted, across every campaign
  // — shows when applied, when reviewed, and whether it was approved
  // (converted into a hire, linked via approved_hire_id) or rejected/expired/
  // withdrawn. Separate from the Hires table below: an application only
  // becomes a hire once a brand approves it, and rejected/expired/withdrawn
  // applications never do. Server-side paginated, same as Hires above.
  const { data: applicationsResult, isLoading: applicationsLoading } = useQuery<{ rows: CreatorApplication[]; total: number }>({
    queryKey: ['creator-applications', id, applicationsPage, applicationsPageSize],
    queryFn: async () => {
      const { data, count, error } = await supabase
        .from('cr_applications')
        .select('id, status, applied_at, reviewed_at, reject_reason, approved_hire_id, cr_campaigns(name, product_name)', { count: 'exact' })
        .eq('creator_id', id)
        .order('applied_at', { ascending: false })
        .range((applicationsPage - 1) * applicationsPageSize, applicationsPage * applicationsPageSize - 1);
      if (error) throw error;
      return { rows: (data ?? []) as unknown as CreatorApplication[], total: count ?? 0 };
    },
    enabled: !!id,
  });
  const applications = applicationsResult?.rows ?? [];
  const applicationsTotal = applicationsResult?.total ?? 0;
  const applicationsTotalPages = Math.max(1, Math.ceil(applicationsTotal / applicationsPageSize));

  if (creatorLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!creator) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-muted-foreground gap-3">
        <p className="text-lg font-medium">Creator not found</p>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ChevronLeft className="h-4 w-4 mr-1" /> Back
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back nav */}
      <div>
        <Button variant="ghost" size="sm" className="gap-1 -ml-2 text-muted-foreground" onClick={() => navigate(-1)}>
          <ChevronLeft className="h-4 w-4" /> Back
        </Button>
      </div>

      {/* Page heading */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Creator Profile</h1>
        <p className="text-muted-foreground text-sm mt-1">Participation activity, hires, and applications for this creator</p>
      </div>

      {/* Creator header */}
      <div className="rounded-lg border bg-card p-5 space-y-4">
        <div className="flex flex-wrap items-start gap-3 justify-between">
          <div className="flex items-start gap-3">
            {creator.avatar_url && (
              <img src={creator.avatar_url} alt={creator.name ?? ''} className="h-14 w-14 rounded-full object-cover border shrink-0" />
            )}
            <div>
              <h2 className="text-xl font-bold">{creator.name ?? '—'}</h2>
              {creator.tiktok_username && (
                <a
                  href={creator.tiktok_url ?? `https://www.tiktok.com/@${creator.tiktok_username}`}
                  target="_blank" rel="noreferrer"
                  className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1"
                >
                  @{creator.tiktok_username} <ExternalLink className="h-3 w-3" />
                </a>
              )}
              {creator.email && <p className="text-xs text-muted-foreground">{creator.email}</p>}
            </div>
          </div>
          {creator.creator_level && <Badge variant="outline">{creator.creator_level}</Badge>}
        </div>

        <Separator />

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">GMV</p>
            <p className="font-medium">{creator.creator_gmv != null ? `$${creator.creator_gmv.toLocaleString()}` : '—'}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Followers</p>
            <p className="font-medium">{creator.follower_count != null ? creator.follower_count.toLocaleString() : '—'}</p>
          </div>
          {creator.categories && creator.categories.length > 0 && (
            <div className="col-span-2">
              <p className="text-xs text-muted-foreground mb-1">Categories</p>
              <div className="flex flex-wrap gap-1">
                {creator.categories.map(c => <Badge key={c} variant="secondary" className="text-xs">{c}</Badge>)}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Creator activity — live participation stats across all campaigns */}
      <div className="rounded-lg border bg-card p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-semibold text-sm">Creator Activity</h2>
          <span className="text-xs text-muted-foreground">— across all campaigns</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard icon={Send}          label="Jobs Applied"     value={activityStats?.jobs_applied_count ?? 0} />
          <StatCard icon={CheckCircle2}  label="Invites Accepted" value={activityStats?.invites_accepted_count ?? 0} color="text-emerald-600" />
          <StatCard icon={ClipboardCheck} label="Jobs Completed"  value={activityStats?.jobs_completed_count ?? 0} color="text-emerald-700" />
          <StatCard
            icon={Clock}
            label="Last Active"
            value={activityStats?.last_active_at ? format(new Date(activityStats.last_active_at), 'MMM d, yyyy') : '—'}
          />
        </div>
      </div>

      {/* Hires — one row per campaign engagement; click through for the
          per-hire invite/lifecycle timeline on HireDetail */}
      <div className="rounded-lg border bg-card">
        <div className="border-b px-5 py-3">
          <h2 className="font-semibold text-sm">Hires ({hiresTotal})</h2>
        </div>
        {hiresLoading ? (
          <div className="p-6 space-y-3">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : hires.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
            <p className="text-sm">No hires yet.</p>
          </div>
        ) : (
          <div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Campaign</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Package</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead>Invited</TableHead>
                  <TableHead>Confirmed</TableHead>
                  <TableHead>Completed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {hires.map(h => (
                  <TableRow
                    key={h.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/creator-payments/hires/${h.id}`)}
                  >
                    <TableCell className="max-w-[220px] truncate">{h.cr_campaigns?.name ?? h.cr_campaigns?.product_name ?? '—'}</TableCell>
                    <TableCell><HireStatusBadge status={h.status} /></TableCell>
                    <TableCell className="text-right">{h.package_size}</TableCell>
                    <TableCell className="text-right">{fmtCents(h.creator_rate_cents)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {h.invited_at ? format(new Date(h.invited_at), 'MMM d, yyyy') : '—'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {h.confirmed_at ? format(new Date(h.confirmed_at), 'MMM d, yyyy') : '—'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {h.completed_at ? format(new Date(h.completed_at), 'MMM d, yyyy') : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {hiresTotal > 0 && (
          <DataPagination
            currentPage={hiresPage}
            totalPages={hiresTotalPages}
            pageSize={hiresPageSize}
            totalItems={hiresTotal}
            onPageChange={setHiresPage}
            onPageSizeChange={(size) => { setHiresPageSize(size); setHiresPage(1); }}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            className="px-5 py-3 border-t"
          />
        )}
      </div>

      {/* Applications — creator-initiated; an approved application converts
          into a hire (approved_hire_id), rejected/expired/withdrawn ones never do */}
      <div className="rounded-lg border bg-card">
        <div className="border-b px-5 py-3">
          <h2 className="font-semibold text-sm">Applications ({applicationsTotal})</h2>
        </div>
        {applicationsLoading ? (
          <div className="p-6 space-y-3">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : applications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
            <p className="text-sm">No applications yet.</p>
          </div>
        ) : (
          <div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Campaign</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Applied</TableHead>
                  <TableHead>Reviewed</TableHead>
                  <TableHead>Note</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {applications.map(a => {
                  const linksToHire = !!a.approved_hire_id;
                  return (
                    <TableRow
                      key={a.id}
                      className={linksToHire ? 'cursor-pointer hover:bg-muted/50' : ''}
                      onClick={linksToHire ? () => navigate(`/creator-payments/hires/${a.approved_hire_id}`) : undefined}
                    >
                      <TableCell className="max-w-[220px] truncate">{a.cr_campaigns?.name ?? a.cr_campaigns?.product_name ?? '—'}</TableCell>
                      <TableCell><ApplicationStatusBadge status={a.status} /></TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {a.applied_at ? format(new Date(a.applied_at), 'MMM d, yyyy') : '—'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {a.reviewed_at ? format(new Date(a.reviewed_at), 'MMM d, yyyy') : '—'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">
                        {a.reject_reason ?? '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        {linksToHire && (
                          <span title="View resulting hire">
                            <ExternalLink className="h-3.5 w-3.5 text-muted-foreground inline-block" />
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        {applicationsTotal > 0 && (
          <DataPagination
            currentPage={applicationsPage}
            totalPages={applicationsTotalPages}
            pageSize={applicationsPageSize}
            totalItems={applicationsTotal}
            onPageChange={setApplicationsPage}
            onPageSizeChange={(size) => { setApplicationsPageSize(size); setApplicationsPage(1); }}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            className="px-5 py-3 border-t"
          />
        )}
      </div>
    </div>
  );
}
